/**
 * O aviso de ação feita do Negócio (revisão da 11.5) — o toast que **sobrevive ao modal**.
 *
 * A troca de Responsável pode tirar o Negócio do alcance de quem a fez: a SDR que entrega a um
 * Vendedor (o handoff leva o Negócio a Vendas, fora do funil dela) e o Vendedor que entrega o
 * próprio Negócio a um colega deixam de enxergá-lo, e o modal fecha. A confirmação "Negócio
 * entregue: agora em {funil} · {etapa}" não pode morar dentro dele — morreria com ele. Ela mora
 * aqui: `anunciar` grava o texto num armazém do módulo, e `RegiaoDeAvisos` — montada **uma vez**,
 * no layout da aplicação — o desenha.
 *
 * **A região `role="status"` é montada vazia desde a abertura do modal** e recebe o texto depois:
 * leitor de tela anuncia mudança numa região viva que já existia, e não uma região que nasce com o
 * texto dentro. O toast (DESIGN.md) some sozinho; nunca é a única confirmação de erro.
 */

import { useEffect, useSyncExternalStore } from 'react'

import './id-do-negocio.css'
import './avisos.css'

/** Quanto tempo o aviso fica na tela. */
export const DURACAO_DO_AVISO_EM_MS = 4000

interface Aviso {
  texto: string
  /** Muda a cada `anunciar`: o mesmo texto duas vezes reinicia a contagem. */
  vez: number
}

let atual: Aviso | null = null
let contador = 0
const ouvintes = new Set<() => void>()

function avisar(): void {
  for (const ouvinte of ouvintes) {
    ouvinte()
  }
}

/** Mostra `texto` na região de avisos — de qualquer lugar, inclusive de um modal que vai fechar. */
export function anunciar(texto: string): void {
  contador += 1
  atual = { texto, vez: contador }
  avisar()
}

/** Apaga o aviso (o temporizador, ou um teste que começa do zero). */
export function limparAviso(): void {
  atual = null
  avisar()
}

function inscrever(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte)
  return () => {
    ouvintes.delete(ouvinte)
  }
}

function lerAviso(): Aviso | null {
  return atual
}

/**
 * A região viva dos avisos — montada uma vez, no layout.
 *
 * `ativa` é "há um modal do Negócio aberto": a região `role="status"` nasce **vazia** com ele, e
 * continua montada enquanto houver aviso — inclusive depois de o modal fechar, que é o caso da
 * entrega. Sem modal e sem aviso ela não existe: as outras telas têm as regiões de espera delas
 * ("Carregando…"), e uma segunda região `status` vazia em toda tela só atrapalharia quem as lê.
 */
export function RegiaoDeAvisos({ ativa = true }: { ativa?: boolean }) {
  const aviso = useSyncExternalStore(inscrever, lerAviso, lerAviso)
  useEffect(() => {
    if (aviso === null) {
      return
    }
    const temporizador = window.setTimeout(() => {
      if (atual?.vez === aviso.vez) {
        limparAviso()
      }
    }, DURACAO_DO_AVISO_EM_MS)
    return () => {
      window.clearTimeout(temporizador)
    }
  }, [aviso])
  if (!ativa && aviso === null) {
    return null
  }
  return (
    <div className="regiao-de-avisos" role="status">
      {aviso === null ? null : <div className="toast">{aviso.texto}</div>}
    </div>
  )
}
